import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'

/**
 * Grant funding reference for music organizations.
 *
 * EVERY figure here is verified against the funder's own published guidelines,
 * and every entry carries its source link. Two editorial rules, both load-bearing:
 *
 * 1. NO DEADLINE COUNTDOWNS. Verified 2026-07-25: every program below had
 *    already closed its then-current cycle, and none had published the next
 *    one. A page built around dates would have shipped stale. It is written as
 *    an evergreen reference — what each program funds and roughly when it runs —
 *    so it stays true between cycles instead of rotting.
 * 2. NOTHING UNVERIFIED. Programs we could not source to published guidelines
 *    were left off rather than hedged. A funding page that is wrong once is
 *    never trusted again.
 *
 * Re-check the sources before adding a program or restating an amount.
 */

/** Verified against each funder's published guidelines on 2026-07-25. */
const PROGRAMS = [
  {
    name: 'NEA Grants for Arts Projects',
    funder: 'National Endowment for the Arts',
    award: '$10,000 to $100,000',
    fit: 'The one that names design outright. Applications choose a discipline, and "Design & Our Town" covers "communications and graphic design" in the program guidelines. Your identity work is not a line item buried in a music grant. It can be the project.',
    requirements: [
      'Five years of arts programming before the deadline',
      'At least $20,000 in operating expenses last fiscal year',
      'A 1:1 match, and in-kind counts alongside cash',
    ],
    cadence: 'Two cycles a year, historically February and July.',
    href: 'https://www.arts.gov/grants/grants-for-arts-projects',
  },
  {
    name: 'Support for Organizations',
    funder: 'New York State Council on the Arts',
    award: '$10,000 to $49,500',
    fit: 'General operating support with marketing as a named budget line rather than a grudging allowance. The application asks you to "describe marketing and advertising and provide examples of successful efforts," and the review rubric rewards it.',
    requirements: [
      'A New York nonprofit, or a sponsored program of one',
      'At least $20,000 in New York arts expenses',
      'A grant cannot exceed half your operating expenses',
    ],
    cadence: 'Annual, historically opening in spring and closing early summer.',
    href: 'https://arts.ny.gov/OpportunityGuidelines',
  },
  {
    name: 'Operating Support',
    funder: 'Minnesota State Arts Board',
    award: 'From $15,000',
    fit: 'Unrestricted operating money, funded in part by the state Legacy Amendment. Marketing is not named as allowable, but it is not on the prohibited list either, and the grant is not itemized to cost categories.',
    requirements: [
      'Two consecutive years of Minnesota arts programming',
      'A two-year average of $174,000 in qualifying expenses, which rules out most small ensembles',
      'At least one paid administrative staff member',
    ],
    cadence: 'Annual, historically mid-January.',
    href: 'https://www.arts.state.mn.us/grants/operating-support.htm',
  },
  {
    name: 'Transient Occupancy Tax Grant Program',
    funder: 'Sacramento County (a model for similar county programs)',
    award: '$5,000 to $35,000',
    fit: 'Hotel-tax money, with arts and culture as one of four named categories and "programs, projects and events that support tourism" among the eligible uses. Worth knowing about if your festival draws visitors from out of town.',
    requirements: [
      'A nonprofit with one completed fiscal year',
      'A physical office in the county',
      'The county allocated $1,000,000 to the program for FY2026-27',
    ],
    cadence: 'Annual, historically opening in January.',
    href: 'https://bdm.saccounty.gov/us/en/tot-grant-program.html',
  },
]

/** Verbatim from the NEA FY27 Grants for Arts Projects program guidelines. */
const NEA_RULES = [
  {
    verdict: 'Allowed',
    quote:
      'Design fees, preparing space for an exhibit, installation or de-installation of art, and community planning are allowable.',
    meaning:
      'Paying a designer is an allowable cost. This is the sentence to put in front of a board that assumes grants only buy performances.',
  },
  {
    verdict: 'Not allowed',
    quote: 'Marketing and fundraising expenses not directly related to the project.',
    meaning:
      'Read the qualifier. Marketing tied to the funded project is allowable. Marketing floating free of one is not.',
  },
  {
    verdict: 'Not allowed',
    quote: 'General operating support, or funding for a full season of programming.',
    meaning:
      'The trap most rebrand applications fall into. A season-wide brand campaign reads as operating support. Scope it to one festival, one premiere, one commission.',
  },
]

export default function GrantsPage() {
  const breadcrumbs = generateBreadcrumbs('/resources/grants', {
    ...breadcrumbLabels,
    '/resources/grants': 'Grant Funding',
  })

  useSEO({
    title: 'Grants That Fund Design and Marketing for Music Organizations',
    description:
      'A reference for music organizations: which grant programs fund branding, marketing, communications, and website work, what their guidelines actually allow, and the rule that gets rebrand applications rejected.',
    url: '/resources/grants',
    canonical: '/resources/grants',
    breadcrumbs,
  })

  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const rulesRef = useScrollReveal<HTMLDivElement>('.rule-card', { stagger: 0.1 })
  const programsRef = useStaggeredGrid<HTMLDivElement>('.program-card', { stagger: 0.1 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  return (
    <div className="mx-auto max-w-5xl space-y-16 px-4 py-16">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-3xl space-y-5 text-center">
        <h1 className="text-4xl font-bold md:text-5xl">Grants That Fund Design Work</h1>
        <p className="text-muted-foreground text-lg">
          Most music organizations treat a rebrand as something to fund out of an operating budget that has no room for
          it. Several grant programs already pay for this work. The question is usually not whether you can afford it.
          It is which application you write.
        </p>
      </section>

      {/* The rule that matters most */}
      <section className="space-y-8">
        <div className="mx-auto max-w-3xl space-y-3 text-center">
          <h2 className="text-3xl font-bold">The rule that decides most applications</h2>
          <p className="text-muted-foreground">
            The NEA funds projects, not organizations. Three lines from its own guidelines explain nearly every accepted
            and rejected design request.
          </p>
        </div>

        <div ref={rulesRef} className="space-y-4">
          {NEA_RULES.map((rule) => (
            <div key={rule.quote} className="rule-card border-border rounded-lg border p-6">
              <div
                className={`text-sm font-semibold tracking-wide uppercase ${
                  rule.verdict === 'Allowed' ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                {rule.verdict}
              </div>
              <blockquote className="border-primary/40 mt-3 border-l-2 pl-4 text-lg italic">
                &ldquo;{rule.quote}&rdquo;
              </blockquote>
              <p className="text-muted-foreground mt-3 text-sm">{rule.meaning}</p>
            </div>
          ))}
        </div>

        <p className="text-muted-foreground mx-auto max-w-3xl text-center text-sm">
          Quoted from the FY27 Grants for Arts Projects program guidelines.{' '}
          <a
            href="https://www.arts.gov/grants/grants-for-arts-projects"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline"
          >
            Read them in full
          </a>
          .
        </p>
      </section>

      {/* Programs */}
      <section className="space-y-8">
        <div className="mx-auto max-w-3xl space-y-3 text-center">
          <h2 className="text-3xl font-bold">Where the money is</h2>
          <p className="text-muted-foreground">
            Four programs whose published guidelines cover design, marketing, or communications work. Every figure below
            comes from the funder&apos;s own documents.
          </p>
        </div>

        <div ref={programsRef} className="grid gap-6 md:grid-cols-2">
          {PROGRAMS.map((p) => (
            <m.div
              key={p.name}
              className="program-card border-border flex flex-col space-y-4 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div>
                <h3 className="text-xl font-bold">{p.name}</h3>
                <div className="text-muted-foreground text-sm">{p.funder}</div>
              </div>
              <div className="text-primary text-lg font-bold">{p.award}</div>
              <p className="text-muted-foreground flex-1 text-sm">{p.fit}</p>
              <ul className="text-muted-foreground space-y-1 text-sm">
                {p.requirements.map((r) => (
                  <li key={r}>&bull; {r}</li>
                ))}
              </ul>
              <div className="text-muted-foreground border-border border-t pt-3 text-sm">
                <span className="font-semibold">Timing.</span> {p.cadence}
              </div>
              <a
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary text-sm font-semibold underline"
              >
                Current guidelines and dates &rarr;
              </a>
            </m.div>
          ))}
        </div>

        <div className="border-border bg-surface mx-auto max-w-3xl rounded-lg border p-6">
          <h3 className="font-semibold">On dates</h3>
          <p className="text-muted-foreground mt-2 text-sm">
            Deadlines move every year, and a page that lists them goes stale the week after it publishes. Each program
            above runs on an annual or twice-yearly rhythm, noted on its card, and the link goes to the funder&apos;s
            own page where the current cycle is posted. Check there before you plan around a date. This list was last
            verified in July 2026.
          </p>
        </div>
      </section>

      {/* Practical notes */}
      <section className="space-y-8">
        <div className="mx-auto max-w-3xl space-y-3 text-center">
          <h2 className="text-3xl font-bold">Before you apply</h2>
        </div>

        <div className="mx-auto max-w-3xl space-y-4">
          <div className="border-border rounded-lg border p-6">
            <h3 className="font-semibold">Scope it to something with edges</h3>
            <p className="text-muted-foreground mt-2 text-sm">
              &ldquo;Refresh our brand&rdquo; reads as operating support and gets declined. &ldquo;Identity and
              materials for our 2027 festival&rdquo; is a project. Same work, different application.
            </p>
          </div>
          <div className="border-border rounded-lg border p-6">
            <h3 className="font-semibold">Your in-kind contribution probably counts</h3>
            <p className="text-muted-foreground mt-2 text-sm">
              The NEA requires a one-to-one match, which stops many organizations before they start. Both cash and
              in-kind support count toward it, and volunteer and donated time is real.
            </p>
          </div>
          <div className="border-border rounded-lg border p-6">
            <h3 className="font-semibold">Check the floor before you spend a week on the form</h3>
            <p className="text-muted-foreground mt-2 text-sm">
              Several programs carry minimums that rule out smaller ensembles. Minnesota&apos;s operating support needs
              a two-year average of $174,000 in expenses. Where you fall below a state threshold, the regional arts
              council is usually the right door instead.
            </p>
          </div>
          <div className="border-border rounded-lg border p-6">
            <h3 className="font-semibold">Ask what your own county does</h3>
            <p className="text-muted-foreground mt-2 text-sm">
              Hotel-tax grant programs exist to bring visitors into a county, which makes a festival an unusually good
              fit. Sacramento County&apos;s is listed above as one example. Whether your county runs something similar
              is worth a phone call.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section ref={ctaRef} className="bg-primary/5 border-primary/20 space-y-6 rounded-lg border p-12 text-center">
        <div className="cta-content">
          <h2 className="text-3xl font-bold">Scoping work to fit a grant</h2>
          <p className="text-muted-foreground mx-auto mt-4 max-w-2xl">
            A brand project written as a season refresh gets declined. Written as one festival&apos;s identity and
            materials, the same work fits. If you are considering an application, we can scope the design work to match
            what the program funds, and give you the budget lines to put in it.
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a href="/book" target="_blank" rel="noopener noreferrer">
              <m.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Talk it through &rarr;
                </Button>
              </m.div>
            </a>
            <Link to="/services/design">
              <m.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  See the design work
                </Button>
              </m.div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
